
/**
 * Utility functions for device capability detection and memory management
 */

/**
 * Check if the device has limited memory
 * Returns true for devices with less than 4GB of RAM
 */
export const isDeviceMemoryLow = (): boolean => {
  if (typeof window !== 'undefined' && 'navigator' in window) {
    // @ts-ignore - deviceMemory is not in all TS definitions
    const deviceMemory = (navigator as any).deviceMemory;
    
    // If deviceMemory is available and less than 4GB
    if (deviceMemory !== undefined && deviceMemory < 4) {
      return true;
    }
    
    // Use heuristics if deviceMemory API isn't available
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Detect mobile devices which typically have less memory
    const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
    const isMobile = mobileKeywords.some(keyword => userAgent.includes(keyword));
    
    if (isMobile) {
      return true;
    }
  }
  
  // Default to false if we can't determine
  return false;
};

/**
 * Get the estimated available memory in MB
 * Returns -1 if not supported
 */
export const getAvailableMemory = (): number => {
  if (typeof window !== 'undefined' && 'performance' in window && 'memory' in performance) {
    // @ts-ignore - memory is not in all TS definitions
    const memory = (performance as any).memory;
    if (memory && memory.jsHeapSizeLimit) {
      const totalHeapSize = memory.jsHeapSizeLimit;
      const usedHeapSize = memory.usedJSHeapSize;
      const availableHeap = totalHeapSize - usedHeapSize;
      
      // Convert to MB
      return Math.floor(availableHeap / (1024 * 1024));
    }
  }
  
  return -1; // Not supported
};

/**
 * Check if the device is a low-powered device
 * This checks processor cores and other factors
 */
export const isLowPoweredDevice = (): boolean => {
  if (typeof window !== 'undefined' && 'navigator' in window) {
    // Check available CPU cores
    if (navigator.hardwareConcurrency !== undefined) {
      // Devices with 2 or fewer cores are considered low-powered
      if (navigator.hardwareConcurrency <= 2) {
        return true;
      }
    }
    
    // Check if the device is a mobile device
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
    const isMobile = mobileKeywords.some(keyword => userAgent.includes(keyword));
    
    // Check if it's an older mobile device
    if (isMobile) {
      // Look for indicators of older devices
      const olderDeviceKeywords = ['android 4', 'android 5', 'iphone os 9', 'iphone os 10'];
      const isOlderDevice = olderDeviceKeywords.some(keyword => userAgent.includes(keyword));
      
      if (isOlderDevice) {
        return true;
      }
    }
  }
  
  return false;
};

/**
 * Determine the optimal cache settings based on device capabilities
 */
export const getOptimalCacheSettings = (): {
  maxMemoryCacheItems: number;
  maxMemoryCacheSize: number;
  maxIndexedDBItems: number;
} => {
  const isLowMemory = isDeviceMemoryLow();
  const isLowPower = isLowPoweredDevice();
  
  // Default settings for high-end devices
  let settings = {
    maxMemoryCacheItems: 100,
    maxMemoryCacheSize: 10 * 1024 * 1024, // 10MB
    maxIndexedDBItems: 1000
  };
  
  // Adjust for low memory devices
  if (isLowMemory) {
    settings = {
      maxMemoryCacheItems: 30,
      maxMemoryCacheSize: 2 * 1024 * 1024, // 2MB
      maxIndexedDBItems: 300
    };
  }
  
  // Further adjust for low power devices
  if (isLowPower) {
    settings.maxMemoryCacheItems = Math.min(settings.maxMemoryCacheItems, 20);
    settings.maxIndexedDBItems = Math.min(settings.maxIndexedDBItems, 200);
  }
  
  return settings;
};

/**
 * Check if the device supports compression/decompression
 */
export const supportsCompression = (): boolean => {
  return typeof window !== 'undefined' && 'CompressionStream' in window;
};

/**
 * Monitor memory usage and trigger cleanup if needed
 * @param cleanupCallback Function to call when memory usage is too high
 */
export const setupMemoryMonitoring = (cleanupCallback: () => void): () => void => {
  if (typeof window === 'undefined') return () => {};
  
  // Create a periodic check
  const interval = setInterval(() => {
    if ('performance' in window && 'memory' in performance) {
      // @ts-ignore - memory is not in all TS definitions
      const memory = (performance as any).memory;
      if (memory) {
        const usedRatio = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
        
        // If using more than 80% of available heap
        if (usedRatio > 0.8) {
          console.warn(`High memory usage detected (${(usedRatio * 100).toFixed(1)}%), triggering cleanup`);
          
          // Call cleanup function
          cleanupCallback();
        }
      }
    }
  }, 60000); // Check every minute
  
  // Return function to clear the interval
  return () => clearInterval(interval);
};
